import {
  FIXED_LAUNCH_SPEED,
  MATH_EPSILON,
  add,
  directionFromAim,
  dot,
  lerp,
  reflect,
  scale,
  sweepPointCapsule,
  sweepPointCircle,
} from './math';
import type {
  FailureReason,
  FlightConfig,
  FlightEvent,
  FlightResult,
  FlightState,
  FlightStep,
  Hazard,
  PhysicsWorld,
  Receiver,
  RelayGrade,
  RelayOutcome,
  ScoreBreakdown,
  ScoreResolution,
  ScoreState,
  Vec2,
} from './types';

export const DEFAULT_FLIGHT_CONFIG: FlightConfig = {
  launchSpeed: FIXED_LAUNCH_SPEED,
  pulseRadius: 5,
  maxBounces: 4,
  maxFlightTime: 1.2,
  fixedStep: 1 / 120,
  contactEpsilon: 0.01,
};

interface CollisionCandidate {
  readonly kind: 'receiver' | 'mirror' | 'hazard' | 'bounds';
  readonly id: string;
  readonly t: number;
  readonly point: Vec2;
  readonly normal: Vec2;
  readonly priority: number;
}

function resolveConfig(overrides: Partial<FlightConfig> | undefined): FlightConfig {
  return { ...DEFAULT_FLIGHT_CONFIG, ...overrides };
}

function chooseEarlier(
  current: CollisionCandidate | null,
  candidate: CollisionCandidate,
): CollisionCandidate {
  if (current === null) return candidate;
  if (candidate.t < current.t - MATH_EPSILON) return candidate;
  if (Math.abs(candidate.t - current.t) <= MATH_EPSILON && candidate.priority < current.priority) {
    return candidate;
  }
  return current;
}

function boundsCollision(
  start: Vec2,
  end: Vec2,
  world: PhysicsWorld,
  radius: number,
): CollisionCandidate | null {
  const movement = { x: end.x - start.x, y: end.y - start.y };
  const minimumX = radius;
  const maximumX = world.width - radius;
  const minimumY = radius;
  const maximumY = world.height - radius;
  const candidates: CollisionCandidate[] = [];

  const addBoundary = (t: number, normal: Vec2): void => {
    if (t < -MATH_EPSILON || t > 1 + MATH_EPSILON) return;
    const clamped = Math.min(1, Math.max(0, t));
    candidates.push({
      kind: 'bounds',
      id: 'world-bounds',
      t: clamped,
      point: lerp(start, end, clamped),
      normal,
      priority: 3,
    });
  };

  if (start.x < minimumX || start.x > maximumX || start.y < minimumY || start.y > maximumY) {
    return {
      kind: 'bounds',
      id: 'world-bounds',
      t: 0,
      point: start,
      normal: { x: 0, y: 0 },
      priority: 3,
    };
  }

  if (movement.x < -MATH_EPSILON && end.x < minimumX) {
    addBoundary((minimumX - start.x) / movement.x, { x: 1, y: 0 });
  } else if (movement.x > MATH_EPSILON && end.x > maximumX) {
    addBoundary((maximumX - start.x) / movement.x, { x: -1, y: 0 });
  }

  if (movement.y < -MATH_EPSILON && end.y < minimumY) {
    addBoundary((minimumY - start.y) / movement.y, { x: 0, y: 1 });
  } else if (movement.y > MATH_EPSILON && end.y > maximumY) {
    addBoundary((maximumY - start.y) / movement.y, { x: 0, y: -1 });
  }

  candidates.sort((left, right) => left.t - right.t);
  return candidates[0] ?? null;
}

function hazardCollision(
  start: Vec2,
  end: Vec2,
  hazard: Hazard,
  pulseRadius: number,
): CollisionCandidate | null {
  const hit =
    hazard.kind === 'circle'
      ? sweepPointCircle(start, end, hazard.center, hazard.radius + pulseRadius)
      : sweepPointCapsule(start, end, hazard, hazard.radius + pulseRadius);
  if (hit === null) return null;
  return {
    kind: 'hazard',
    id: hazard.id,
    t: hit.t,
    point: hit.point,
    normal: hit.normal,
    priority: 0,
  };
}

function findCollision(
  state: FlightState,
  end: Vec2,
  world: PhysicsWorld,
  config: FlightConfig,
): CollisionCandidate | null {
  let earliest = boundsCollision(state.position, end, world, config.pulseRadius);

  for (const receiver of world.receivers) {
    if (receiver.id === state.sourceReceiverId) continue;
    const hit = sweepPointCircle(
      state.position,
      end,
      receiver.center,
      receiver.radius + config.pulseRadius,
    );
    if (hit === null) continue;
    earliest = chooseEarlier(earliest, {
      kind: 'receiver',
      id: receiver.id,
      t: hit.t,
      point: hit.point,
      normal: hit.normal,
      priority: 1,
    });
  }

  for (const hazard of world.hazards) {
    const candidate = hazardCollision(state.position, end, hazard, config.pulseRadius);
    if (candidate !== null) earliest = chooseEarlier(earliest, candidate);
  }

  const movement = { x: end.x - state.position.x, y: end.y - state.position.y };
  for (const mirror of world.mirrors) {
    const hit = sweepPointCapsule(
      state.position,
      end,
      mirror,
      config.pulseRadius + mirror.thickness / 2,
    );
    if (hit === null || dot(movement, hit.normal) >= -MATH_EPSILON) continue;
    earliest = chooseEarlier(earliest, {
      kind: 'mirror',
      id: mirror.id,
      t: hit.t,
      point: hit.point,
      normal: hit.normal,
      priority: 2,
    });
  }

  return earliest;
}

function collectShards(
  state: FlightState,
  start: Vec2,
  end: Vec2,
  startTime: number,
  duration: number,
  world: PhysicsWorld,
  pulseRadius: number,
): { readonly ids: readonly string[]; readonly events: readonly FlightEvent[] } {
  const knownIds = new Set(state.collectedShardIds);
  const hits: Array<{ readonly id: string; readonly t: number; readonly point: Vec2 }> = [];

  for (const shard of world.shards ?? []) {
    if (knownIds.has(shard.id)) continue;
    const hit = sweepPointCircle(start, end, shard.center, shard.radius + pulseRadius);
    if (hit !== null) hits.push({ id: shard.id, t: hit.t, point: hit.point });
  }

  hits.sort((left, right) => left.t - right.t || left.id.localeCompare(right.id));
  const events: FlightEvent[] = [];
  for (const hit of hits) {
    if (knownIds.has(hit.id)) continue;
    knownIds.add(hit.id);
    events.push({
      type: 'shard',
      id: hit.id,
      point: hit.point,
      time: startTime + duration * hit.t,
    });
  }

  return { ids: [...knownIds], events };
}

function failedState(
  state: FlightState,
  reason: FailureReason,
  position: Vec2 = state.position,
): FlightState {
  return { ...state, position, status: 'failed', failureReason: reason };
}

export function createFlight(
  source: Receiver,
  aimRadians: number,
  overrides?: Partial<FlightConfig>,
): FlightState {
  const config = resolveConfig(overrides);
  const velocity = scale(directionFromAim(aimRadians), config.launchSpeed);
  return {
    position: source.center,
    velocity,
    elapsed: 0,
    bounces: 0,
    status: 'flying',
    sourceReceiverId: source.id,
    collectedShardIds: [],
  };
}

export function stepFlight(
  initialState: FlightState,
  world: PhysicsWorld,
  deltaSeconds: number,
  overrides?: Partial<FlightConfig>,
): FlightStep {
  const config = resolveConfig(overrides);
  if (initialState.status !== 'flying' || deltaSeconds <= 0) {
    return { state: initialState, events: [] };
  }

  const timeBudget = Math.max(0, config.maxFlightTime - initialState.elapsed);
  let remaining = Math.min(deltaSeconds, timeBudget);
  let state = initialState;
  const events: FlightEvent[] = [];
  let iterations = 0;

  while (remaining > MATH_EPSILON && state.status === 'flying') {
    iterations += 1;
    if (iterations > config.maxBounces + 4) {
      state = failedState(state, 'bounce-limit');
      events.push({
        type: 'failed',
        reason: 'bounce-limit',
        point: state.position,
        time: state.elapsed,
      });
      break;
    }

    const start = state.position;
    const end = add(start, scale(state.velocity, remaining));
    const collision = findCollision(state, end, world, config);
    const fraction = collision?.t ?? 1;
    const travelDuration = remaining * fraction;
    const nextPosition = collision?.point ?? end;
    const collected = collectShards(
      state,
      start,
      nextPosition,
      state.elapsed,
      travelDuration,
      world,
      config.pulseRadius,
    );
    events.push(...collected.events);
    state = {
      ...state,
      position: nextPosition,
      elapsed: state.elapsed + travelDuration,
      collectedShardIds: collected.ids,
    };
    remaining -= travelDuration;

    if (collision === null) break;

    if (collision.kind === 'receiver') {
      state = { ...state, status: 'hit', hitReceiverId: collision.id };
      events.push({
        type: 'receiver',
        id: collision.id,
        point: collision.point,
        time: state.elapsed,
      });
      break;
    }

    if (collision.kind === 'hazard') {
      state = failedState(state, 'hazard');
      events.push({
        type: 'hazard',
        id: collision.id,
        point: collision.point,
        time: state.elapsed,
      });
      events.push({
        type: 'failed',
        reason: 'hazard',
        point: collision.point,
        time: state.elapsed,
      });
      break;
    }

    if (collision.kind === 'bounds') {
      state = failedState(state, 'bounds');
      events.push({
        type: 'failed',
        reason: 'bounds',
        point: collision.point,
        time: state.elapsed,
      });
      break;
    }

    if (state.bounces >= config.maxBounces) {
      state = failedState(state, 'bounce-limit');
      events.push({
        type: 'failed',
        reason: 'bounce-limit',
        point: collision.point,
        time: state.elapsed,
      });
      break;
    }

    const reflectedVelocity = reflect(state.velocity, collision.normal);
    const bounce = state.bounces + 1;
    state = {
      ...state,
      position: add(collision.point, scale(collision.normal, config.contactEpsilon)),
      velocity: reflectedVelocity,
      bounces: bounce,
    };
    events.push({
      type: 'mirror',
      id: collision.id,
      point: collision.point,
      normal: collision.normal,
      time: state.elapsed,
      bounce,
    });
  }

  if (state.status === 'flying' && state.elapsed >= config.maxFlightTime - MATH_EPSILON) {
    state = failedState(state, 'timeout');
    events.push({
      type: 'failed',
      reason: 'timeout',
      point: state.position,
      time: state.elapsed,
    });
  }

  return { state, events };
}

export function simulateFlight(
  world: PhysicsWorld,
  sourceReceiverId: string,
  aimRadians: number,
  overrides?: Partial<FlightConfig>,
): FlightResult {
  const source = world.receivers.find((receiver) => receiver.id === sourceReceiverId);
  if (source === undefined) throw new Error(`Unknown source receiver: ${sourceReceiverId}`);
  const config = resolveConfig(overrides);
  let state = createFlight(source, aimRadians, config);
  const events: FlightEvent[] = [];
  const path: Vec2[] = [state.position];

  while (state.status === 'flying') {
    const step = stepFlight(state, world, config.fixedStep, config);
    state = step.state;
    events.push(...step.events);
    for (const event of step.events) {
      if (event.type !== 'shard') path.push(event.point);
    }
    const last = path[path.length - 1];
    if (last === undefined || last.x !== state.position.x || last.y !== state.position.y) {
      path.push(state.position);
    }
  }

  return { state, events, path };
}

export function relayGrade(outcome: RelayOutcome): RelayGrade {
  const normalizedOffset = outcome.targetRadius > 0 ? outcome.hitOffset / outcome.targetRadius : 1;
  if (normalizedOffset <= 0.25) return 'center';
  if (normalizedOffset <= 0.6) return 'clean';
  return 'edge';
}

export function comboMultiplier(combo: number): number {
  return 1 + Math.min(8, Math.max(0, combo - 1)) * 0.12;
}

export function createScoreState(): ScoreState {
  return { total: 0, combo: 0, cleanChain: 0, overdriveRelays: 0 };
}

export function resetCombo(state: ScoreState): ScoreState {
  return { ...state, combo: 0, cleanChain: 0, overdriveRelays: 0 };
}

export function resolveRelayScore(state: ScoreState, outcome: RelayOutcome): ScoreResolution {
  const emptyBreakdown: ScoreBreakdown = { base: 0, precision: 0, bank: 0, shards: 0, speed: 0 };
  if (!outcome.successful) {
    return {
      state: resetCombo(state),
      points: 0,
      grade: null,
      bankShot: false,
      overdriveTriggered: false,
      multiplier: 1,
      breakdown: emptyBreakdown,
    };
  }

  const grade = relayGrade(outcome);
  const combo = state.combo + 1;
  const overdriveWasActive = state.overdriveRelays > 0;
  const isClean = grade === 'center' || grade === 'clean';
  let cleanChain = overdriveWasActive ? 0 : isClean ? state.cleanChain + 1 : 0;
  const overdriveTriggered = !overdriveWasActive && cleanChain >= 3;
  let overdriveRelays = overdriveWasActive ? state.overdriveRelays - 1 : 0;
  if (overdriveTriggered) {
    overdriveRelays = 3;
    cleanChain = 0;
  }

  const breakdown: ScoreBreakdown = {
    base: 100,
    precision: grade === 'center' ? 100 : grade === 'clean' ? 45 : 0,
    bank: Math.max(0, outcome.bounces) * 55,
    shards: Math.max(0, outcome.shards) * 30,
    speed: outcome.flightTime <= 0.8 ? 25 : outcome.flightTime <= 1 ? 10 : 0,
  };
  const multiplier = comboMultiplier(combo) * (overdriveWasActive ? 1.5 : 1);
  const rawPoints =
    breakdown.base +
    breakdown.precision +
    breakdown.bank +
    breakdown.shards +
    breakdown.speed;
  const points = Math.round(rawPoints * multiplier);

  return {
    state: {
      total: state.total + points,
      combo,
      cleanChain,
      overdriveRelays,
    },
    points,
    grade,
    bankShot: outcome.bounces > 0,
    overdriveTriggered,
    multiplier,
    breakdown,
  };
}
